// AppsFlyer — Mobile Measurement Partner integration (iOS-first).
//
// Scope
// -----
// Native iOS ONLY for now (web + Android early-return). AppsFlyer
// covers Meta Ads + Apple Search Ads + Google Ads + TikTok Ads +
// organic all in a single dashboard, and forwards purchase/subscribe
// events to Meta's Conversions API for SKAN 4 + AEM optimisation.
//
// Coexists with Postback.sh — both SDKs can run in parallel and each
// still gets its own attribution signal. When we sunset Postback later,
// we only need to remove the postback.js wiring — AppsFlyer stays.
//
// Architecture
// ------------
// The AppsFlyer iOS/Android SDK is shipped as the npm package
// `appsflyer-capacitor-plugin` (installed via `yarn add
// appsflyer-capacitor-plugin@6.18.0` — must pin to 6.x for Capacitor 7
// compatibility; 7.x targets Capacitor 8). We resolve the plugin proxy
// via Capacitor's `registerPlugin("AppsFlyerPlugin")` so this file
// compiles even if the npm package isn't yet installed (web dev
// bundles). Every call is behind an availability check + try/catch
// so a missing plugin degrades silently to a no-op.

import { registerPlugin, Capacitor } from "@capacitor/core";
import { isNativeApp, nativePlatform } from "@/lib/native";
import { attDecided } from "@/lib/att";

const AppsFlyerNative = registerPlugin("AppsFlyerPlugin");

const DEV_KEY = process.env.REACT_APP_APPSFLYER_DEV_KEY || "";
const APP_ID  = process.env.REACT_APP_APPSFLYER_APP_ID  || "";

let _initialized = false;
let _starting = false;
let _privacyMode = false;
let _lastInitError = null;
let _lastEvent = null; // { name, values, at }

/** Diagnostic snapshot used by the in-app debug panel (5-tap on version
 *  footer). Returns everything a support engineer needs to eyeball
 *  whether the SDK is wired up — WITHOUT exposing the raw dev key
 *  (only the first/last 4 chars are shown). Safe to call from web /
 *  any platform — never touches the native bridge. */
export function getAppsFlyerDiagnostics() {
  const mask = (s) => (s && s.length > 8 ? `${s.slice(0, 4)}…${s.slice(-4)}` : s || "(empty)");
  let pluginAvailable = false;
  try { pluginAvailable = !!(Capacitor.isPluginAvailable && Capacitor.isPluginAvailable("AppsFlyerPlugin")); }
  catch (_) { pluginAvailable = false; }
  return {
    isNative: isNativeApp(),
    platform: nativePlatform(),
    pluginAvailable,
    enabled: isAppsFlyerEnabled(),
    devKey: mask(DEV_KEY),
    devKeyPresent: !!DEV_KEY,
    appId: APP_ID || "(empty)",
    initialized: _initialized,
    lastInitError: _lastInitError ? String(_lastInitError.message || _lastInitError) : null,
    lastEvent: _lastEvent,
  };
}

/** iOS-only for MVP. Android will be enabled in Build 28 once we've
 *  verified iOS attribution accuracy end-to-end. Web is always false —
 *  Meta Pixel + GTM already covers the web funnel. */
export function isAppsFlyerEnabled() {
  if (!isNativeApp()) return false;
  const p = nativePlatform();
  if (p !== "ios") return false;
  if (!DEV_KEY || !APP_ID) return false;
  try {
    return Capacitor.isPluginAvailable && Capacitor.isPluginAvailable("AppsFlyerPlugin");
  } catch {
    return false;
  }
}

/**
 * Boot the AppsFlyer SDK. Idempotent — safe to call from multiple
 * mount points. `waitForATTUserAuthorization=60` tells the SDK to
 * pause sending its first attribution payload for up to 60 seconds
 * so the ATT prompt result (Allow / Deny) is included in the very
 * first install postback — critical for Meta's per-install match
 * rate.
 *
 * `customerUserId` is our Calofay `users._id` so AppsFlyer can join
 * install → subscribe → renewal into a single row across our web
 * and native funnels (RevenueCat webhook payloads already carry the
 * same id).
 */
export async function initAppsFlyer(userId) {
  if (!isAppsFlyerEnabled()) return;
  if (_initialized) {
    // SDK already up — just refresh the customer user id if it
    // changed (login flow, account switch). Never in privacy mode.
    if (userId && !_privacyMode) {
      try { await AppsFlyerNative.setCustomerUserId({ customerUserId: String(userId) }); }
      catch (_) {}
    }
    return;
  }
  // Never start the SDK before the user has answered Apple's tracking prompt.
  const decision = await Promise.race([attDecided, new Promise((r) => setTimeout(r, 90000))]);
  // Admin switch off → no popup; SDK runs in privacy mode: no device IDs, no user id,
  // only Apple's anonymous SKAdNetwork conversion values + in-app events.
  _privacyMode = decision === "disabled";
  if (_initialized || _starting) return;
  _starting = true;
  if (_privacyMode) {
    try { await AppsFlyerNative.disableAdvertisingIdentifier({ shouldDisable: true }); } catch (_) {}
    try { await AppsFlyerNative.disableCollectASA({ shouldDisable: true }); } catch (_) {}
  }
  try {
    await AppsFlyerNative.initSDK({
      devKey: DEV_KEY,
      appID: APP_ID,
      // Debug mode: OFF in production. Verified working end-to-end on
      // Feb 26 2026 (AppsFlyer wizard returned "Passed" with
      // af_message: "organic install"). Flip to `true` temporarily if
      // troubleshooting attribution — the in-app diagnostic panel
      // (5-tap Settings version footer) is the preferred debug path.
      isDebug: false,
      waitForATTUserAuthorization: _privacyMode ? 0 : 60,
      registerConversionListener: true,
      registerOnAppOpenAttribution: true,
      registerOnDeepLink: false,
      minTimeBetweenSessions: 6,
      ...(userId && !_privacyMode ? { customerUserId: String(userId) } : {}),
    });
    _initialized = true;
    _lastInitError = null;
    // Fire session_start explicitly so Meta's Conversions API sees
    // the very first session even before any user action. The SDK
    // already tracks sessions natively; this is a belt-and-braces
    // signal that helps SKAN 4 lock the conversion value quickly.
  } catch (e) {
    _lastInitError = e;
    console.warn("[AppsFlyer] initSDK failed", e);
  }
}

/** Return the install-scoped AppsFlyer UID (also called `appsflyer_id`).
 *  Used to plumb the id into RevenueCat as a subscriber attribute so
 *  RC webhook events (RENEWAL, EXPIRATION, REFUND) can be joined to
 *  the original AppsFlyer install months after the initial purchase. */
export async function getAppsFlyerUID() {
  if (_privacyMode) return null; // no per-user linking in privacy mode
  if (!isAppsFlyerEnabled()) return null;
  try {
    const res = await AppsFlyerNative.getAppsFlyerUID();
    return res?.uid || res?.appsFlyerUID || null;
  } catch (e) {
    console.warn("[AppsFlyer] getAppsFlyerUID failed", e);
    return null;
  }
}

/**
 * Log an AppsFlyer in-app event. Names should use AF's `af_*`
 * predefined taxonomy where possible (Meta and AppsFlyer both
 * recognise them out-of-the-box):
 *   af_purchase, af_subscribe, af_start_trial, af_initiated_checkout,
 *   af_add_payment_info, af_content_view, af_search, af_share.
 * `values` must use numeric `af_revenue` (no currency symbols) and
 * 3-letter ISO `af_currency` — the SDK silently drops malformed
 * revenue fields.
 */
export async function logEvent(name, values = {}) {
  if (!isAppsFlyerEnabled()) return { ok: false, reason: "sdk-disabled" };
  try {
    await AppsFlyerNative.logEvent({ eventName: name, eventValues: values });
    _lastEvent = { name, values, at: new Date().toISOString() };
    return { ok: true };
  } catch (e) {
    console.warn("[AppsFlyer] logEvent failed", name, e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ─── High-level event helpers ────────────────────────────────────
// Mirror our existing Postback event shape so callers stay symmetric.

/** Fires when user commits to a plan (taps Buy → Apple sheet opens). */
export async function trackInitiateCheckout({ productId, revenue, currency = "USD" }) {
  return logEvent("af_initiated_checkout", {
    af_content_id: productId,
    af_price: Number(revenue) || 0,
    af_currency: currency,
  });
}

/** Fires the moment StoreKit confirms a paid purchase. */
export async function trackPurchase({ productId, revenue, currency = "USD", transactionId }) {
  return logEvent("af_purchase", {
    af_content_id: productId,
    af_revenue: Number(revenue) || 0,
    af_currency: currency,
    ...(transactionId ? { af_receipt_id: String(transactionId) } : {}),
  });
}

/** Fires when Apple confirms a free trial has started (not a real
 *  charge yet — used for SKAN 4 conversion value tiering). */
export async function trackStartTrial({ productId, currency = "USD" }) {
  return logEvent("af_start_trial", {
    af_content_id: productId,
    af_currency: currency,
  });
}
